import React from 'react';
import { connect, useSelector } from 'react-redux';
import { Box, Card, Typography } from '@drone-js/core';
import { selectIsAnyOverlayVisible } from 'store/slice/overlay';
import { innerHeight } from 'core-framework/window/screen';
import { defaultExpandedAccordion, NavigationHashEnum } from 'core/type/Details.types';
import { ElementEventIds } from '@agoda/analytics-messages-definition-activities';
import { StickyTopBar } from 'component/common/StickyTopBar/StickyTopBar';
import { selectScreenSize } from 'store/slice/screen';
// ----- old detail components -----
import ActivityDetailsFooterDeprecatedDetail from 'component/page/deprecatedDetail/ActivityDetailsFooter/ActivityDetailsFooter';
// ----- new detail components -----
import OfferCardFooter from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/OfferCardFooter';
import { OfferCardFooterType } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/OfferCardFooter.selectors';
import DetailPageTabsComponent from 'component/page/detail/DetailPageTabs/DetailPageTabs';
import { KeyInfo } from 'component/page/detail/DetailCard/KeyInfos/KeyInfo';
import { DetailsSectionTab } from 'component/page/detail/DetailPageContent/DetailsSectionTab';
import DetailCard from 'component/page/detail/DetailCard/DetailCard';
import MosaicGallery from 'component/page/detail/DetailCard/ActivityCarousel/MosaicGallery';
import { DetailPageContentProps, FooterType, mapDispatchToProps, mapStateToProps } from 'component/page/detail/DetailPageContent/DetailPageContent.selectors';
import ActivityOverview from 'component/page/detail/DetailCard/Overview/ActivityOverview';
import FloatingSidePanel from 'component/page/detail/FloatingSidePanel/FloatingSidePanel';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import ContentContainer from 'component/common/ContentContainer/ContentContainer';
import { FloatingPanelContext } from 'component/page/detail/FloatingSidePanel/FloatingPanelContext';
import ActivitiesHighlights from 'component/page/detail/Highlights/ActivitiesHighlights';
import { CartFloatingContainer } from 'component/common/CartFloatingButtonContainer/CartFloatingButtonContainer';
import { ActivitiesToastNotification } from 'component/common/ActToastNotification/ActivitiesToastNotification';
import AvailableDetailPrice from 'component/page/detail/AvailableDetailPrice/AvailableDetailPrice';

const SIDE_PANEL_WIDTH = '351px';

const ViewportHeight: React.CSSProperties = { height: `${innerHeight}px` };

interface FooterProps {
    footerType: FooterType;
    isDetailPageRefactor: boolean;
}

const Footer: React.FC<FooterProps> = ({ footerType, isDetailPageRefactor }) => {
    switch (footerType) {
        case FooterType.Booking:
            // We only have one DetailsFooter so we use first index
            return <OfferCardFooter footerType={OfferCardFooterType.Sticky} offerIndex={0} />;
        case FooterType.Details:
            return isDetailPageRefactor ? <AvailableDetailPrice.Footer /> : <ActivityDetailsFooterDeprecatedDetail />;
        default:
            return null;
    }
};

Footer.displayName = 'ActivitiesDetailsFooter';

const DetailPageContent: React.FC<DetailPageContentProps> = (props: DetailPageContentProps) => {
    const isAnyOverlayVisible: boolean = useSelector(selectIsAnyOverlayVisible);
    const screenSize = useSelector(selectScreenSize);
    const onToggle = (selector: string | undefined, eventIds: ElementEventIds) => (isExpanded: boolean) => props.sendAccordionClickAnalytics(isExpanded, selector || '', eventIds);
    const tabs = props.tabs;
    const [reviewHref, useReviewHref] = React.useState<string>('');
    const spacing = screenSize.desktop ? '24' : '16';
    const { isFloatingPanelVisible } = React.useContext(FloatingPanelContext);
    const footerWrapperRef = React.useRef<HTMLDivElement>(null);
    const [footerHeight, setFooterHeight] = React.useState(0);

    React.useEffect(() => {
        if (footerWrapperRef.current) {
            setFooterHeight(footerWrapperRef.current.offsetHeight);
        }
    }, []);

    // Required to get the height of the footer when it changes dynamically and need an observer for calculate the updated height.
    React.useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === footerWrapperRef.current) {
                    setFooterHeight(entry.contentRect.height);
                }
            }
        });

        const currentFooterWrapper = footerWrapperRef.current;
        if (footerWrapperRef.current) {
            resizeObserver.observe(footerWrapperRef.current);
        }

        return () => {
            if (currentFooterWrapper) {
                resizeObserver.unobserve(currentFooterWrapper);
            }
        };
    }, []);

    React.useEffect(() => {
        tabs.forEach((tab) => {
            if (defaultExpandedAccordion[tab.id]) {
                props.setExpandedAccordionOnDetailsPage(tab.href);
            }
            if (tab.id === NavigationHashEnum.ReviewSection) {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                useReviewHref(tab.href);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="activities-detail-page" data-prerender-name="activities-detail-page" style={ViewportHeight}>
            <Feature.Off name={FeatureName.PageContainerSpacing}>
                <Box
                    minHeight="100vh"
                    px={spacing}
                    py="0"
                    pt="8"
                    pb="12"
                    data-testid="details-page"
                    aria-hidden={isAnyOverlayVisible}
                    backgroundColor="base.surface"
                    borderColor="base.default"
                    borderWidth={['1', '0', '0', '0']}
                    borderStyle="solid"
                >
                    <div className="DetailContentArea align-center">
                        <Box mb="16">
                            <DetailCard seeReviewHref={reviewHref} />
                        </Box>
                        <MosaicGallery />
                        <Box width="100%" display="flex" flexDirection="row" pt={screenSize.desktop ? '32' : '16'}>
                            <Box pb="12" flex={1} minWidth={screenSize.desktop ? `calc(100% - ${SIDE_PANEL_WIDTH})` : '100%'}>
                                <Card p="24" mb="24" variant="filled">
                                    <Typography variant="heading-l" mb="12">
                                        {props.cms[337252]}
                                    </Typography>
                                    <KeyInfo />
                                    <ActivityOverview />
                                </Card>
                                <Feature.On name={FeatureName.ActivitiesHighlights}>
                                    <ActivitiesHighlights />
                                </Feature.On>
                                <StickyTopBar elementName="activities-nav-tabs-container" zIndex={3} noShadow={true} fullWidth={true} noBorder={true}>
                                    <DetailPageTabsComponent />
                                </StickyTopBar>
                                <Box width="100%">
                                    {tabs.map((tab) => {
                                        return <DetailsSectionTab key={tab.id} tab={tab} onToggle={onToggle} isScrolling={props.isScrolling} />;
                                    })}
                                </Box>
                            </Box>

                            {screenSize.desktop && (
                                <Box width={SIDE_PANEL_WIDTH} position="sticky" top="16px" height="100%" minHeight="200px" ms="24">
                                    {isFloatingPanelVisible && <FloatingSidePanel />}
                                </Box>
                            )}
                        </Box>
                    </div>
                </Box>
            </Feature.Off>
            <Feature.On name={FeatureName.PageContainerSpacing}>
                <Box
                    overflowY={isAnyOverlayVisible ? 'hidden' : undefined}
                    aria-hidden={isAnyOverlayVisible}
                    backgroundColor="base.surface"
                    borderColor="base.default"
                    borderWidth={['1', '0', '0', '0']}
                    borderStyle="solid"
                >
                    <ContentContainer data-testid="details-page">
                        <div className="DetailContentArea align-center">
                            <Box mb="16">
                                <DetailCard seeReviewHref={reviewHref} />
                            </Box>
                            <MosaicGallery />
                            <Box width="100%" display="flex" flexDirection="row" pt={screenSize.desktop ? '32' : '16'}>
                                <Box pb="12" flex={1} minWidth={screenSize.desktop ? `calc(100% - ${SIDE_PANEL_WIDTH})` : '100%'}>
                                    <Card p="24" mb="24" variant="filled">
                                        <Typography variant="heading-l" mb="12">
                                            {props.cms[337252]}
                                        </Typography>
                                        <KeyInfo />
                                        <ActivityOverview />
                                    </Card>
                                    <Feature.On name={FeatureName.ActivitiesHighlights}>
                                        <ActivitiesHighlights />
                                    </Feature.On>
                                    <StickyTopBar
                                        elementName="activities-nav-tabs-container"
                                        data-testid="activities-nav-tabs-container"
                                        zIndex={3}
                                        noShadow={true}
                                        fullWidth={true}
                                        noBorder={true}
                                    >
                                        <DetailPageTabsComponent />
                                    </StickyTopBar>
                                    <Box width="100%">
                                        {tabs.map((tab) => {
                                            return <DetailsSectionTab key={tab.id} tab={tab} onToggle={onToggle} isScrolling={props.isScrolling} />;
                                        })}
                                    </Box>
                                </Box>

                                {screenSize.desktop && (
                                    <Box width={SIDE_PANEL_WIDTH} position="sticky" top="16px" height="100%" minHeight="200px" ms="24">
                                        {isFloatingPanelVisible && <FloatingSidePanel />}
                                    </Box>
                                )}
                            </Box>
                        </div>
                    </ContentContainer>
                </Box>
            </Feature.On>
            <Feature.On name={FeatureName.ContinueYourBooking}>
                <Box
                    position="sticky"
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    bottom={screenSize.mobile || (screenSize.tablet && !screenSize.desktop) ? `${footerHeight}px` : 0}
                    zIndex={4}
                >
                    <ActivitiesToastNotification />
                </Box>
            </Feature.On>
            <Box ref={footerWrapperRef} data-test-id="FooterWrapper" position="sticky" display="flex" flexDirection="column" bottom="0" zIndex={4}>
                <CartFloatingContainer position="static" />
                {!screenSize.desktop && <Footer footerType={props.footerType} isDetailPageRefactor={props.isDetailPageRefactor} />}
            </Box>
        </div>
    );
};
DetailPageContent.displayName = 'DetailPageContent';

export default connect(mapStateToProps, mapDispatchToProps)(DetailPageContent);
